<template>
	<PopUpMessage
		:visible="isOpen"
		custom-body-classes="lg:rem:max-w-[920px] lg:mx-auto"
		@update:visible="onUpdateVisible"
	>
		<div class="text-center px-4 lg:px-8 py-8 lg:py-12">
			<h2 class="typo-h1 text-heading-primary mb-2">
				Jak s rozpracovaným nákupním seznamem?
			</h2>
			<p class="typo-body-s mx-auto text-body-secondary">
				Proběhlo přihlášení k zákaznickému účtu. Rozhodněte, co uděláte s vaším nákupním seznamem vytvořeným před přihlášením.
			</p>

			<section class="mt-4 lg:mt-8 lg:px-12">
				<h3 class="typo-h4 mb-4">
					Spojit se stávajícím seznamem
				</h3>

				<div class="grid gap-2 items-end md:grid-cols-[1fr_auto_1fr]">
					<div class="text-left">
						<p class="typo-body-s text-body-secondary mb-2">
							Rozpracovaný seznam
						</p>
						<div class="rounded-lg border border-border-secondary bg-background-primary px-4 py-2 flex items-center">
							<ShoppingListCompactPreview
								v-if="guestList"
								with-date-suffix
								:name="guestList.Name"
								:items="guestPreviewItems"
							/>
						</div>
					</div>

					<p class="typo-body-s text-body-secondary hidden md:block pb-3.5">
						Do
					</p>

					<FormKit
						v-model="targetListId"
						type="shoppingListMergeSelect"
						label="Vyberte cílový seznam"
						:options="targetListOptions"
						placeholder="Vyberte seznam"
						:disabled="isLoadingTargets || isMerging || !targetListOptions.length"
						:classes="{outer: 'w-full text-left'}"
					/>
				</div>

				<p class="flex items-start justify-center gap-2 mt-4 typo-body-s text-body-secondary text-center">
					<NuxtIcon name="info" filled class="text-icon-tertiary shrink-0 mt-0.5" />
					<span>
						Stejné položky se sloučí a zůstane nejvyšší počet z jednoho seznamu a nové položky se přidají.
					</span>
				</p>

				<div v-if="isMerging" class="mt-4 mx-auto max-w-[18.75rem]">
					<div class="h-2 w-full rounded-full bg-light-600 overflow-hidden">
						<div
							class="h-full bg-green-600 transition-all duration-300"
							:style="{width: `${progressPercent}%`}"
						/>
					</div>
					<p class="typo-body-s mt-2 text-neutral-600">
						Sloučeno {{ progress.current }} z {{ progress.total }}
					</p>
				</div>

				<p v-if="error" class="typo-body-s mt-4 text-functional-error-primary">
					{{ error }}
				</p>

				<div class="dialog-controls mt-4">
					<button
						type="button"
						class="btn btn-primary btn-lg w-full justify-center sm:min-w-[12.5rem] md:w-auto"
						:disabled="isMerging || isLoadingTargets || !targetListId"
						@click="merge"
					>
						<Loading :is-loading="isMerging">
							Spojit seznamy
						</Loading>
					</button>
				</div>
			</section>

			<div class="relative my-8 md:my-10">
				<hr class="border-border-secondary" />
				<span class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background-primary px-4 typo-body-s text-body-secondary">
					Nebo
				</span>
			</div>

			<section class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between lg:px-10">
				<div class="rounded-lg border border-border-secondary bg-background-primary px-4 py-2 flex items-center flex-1 min-w-0">
					<ShoppingListCompactPreview
						v-if="guestList"
						with-date-suffix
						:name="guestList.Name"
						:items="guestPreviewItems"
					/>
				</div>
				<div class="flex flex-col gap-3 sm:flex-row sm:justify-center md:shrink-0">
					<button
						type="button"
						class="btn btn-secondary btn-lg justify-center gap-2"
						:disabled="isMerging || isLoadingTargets"
						@click="saveAsNew"
					>
						<Loading :is-loading="isMerging" class="flex items-center flex-nowrap gap-2">
							<NuxtIcon name="bookmark" filled class="text-xl text-icon-primary" />
							Uložit jako nový
						</Loading>
					</button>
					<button
						type="button"
						class="btn btn-secondary btn-lg justify-center gap-2"
						:disabled="isMerging"
						@click="discard"
					>
						<NuxtIcon name="trash-2" filled class="text-xl text-functional-error-primary" />
						Seznam zahodit
					</button>
				</div>
			</section>
		</div>
	</PopUpMessage>
</template>

<script setup lang="ts">
	import Loading from '~/components/ui/Loading.vue';
	import useGuestShoppingListMerge from '~/composables/useGuestShoppingListMerge';

	const {
		isOpen,
		isMerging,
		isLoadingTargets,
		error,
		progress,
		guestList,
		guestPreviewItems,
		targetListId,
		targetListOptions,
		merge,
		saveAsNew,
		discard,
		dismiss,
	} = useGuestShoppingListMerge();

	const progressPercent = computed(() => (
		progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0
	));

	function onUpdateVisible(visible: boolean) {
		if (!visible) {
			dismiss();
		}
	}
</script>
